import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import XtramModal, { XtramButton, XtramCheckbox, XtramField, XtramSelect } from '../../../Widgets/XtramModal/XtramModal';
import ReportsService from '../../../../Services/reports.service.js';
import WithholdingUnpaidPaymentsTab from './WithholdingUnpaidPaymentsTab.jsx';
import { PeriodFields, ReportPreview, ReportTypePicker } from './WithholdingModalParts.jsx';
import styles from './WithholdingModal.module.scss';

const REPORT_TYPES = [
    { value: 'yearlyCertificate', label: 'AnnualWithholdingTaxCertificate' },
    { value: 'yearlyReport', label: 'AnnualWithholdingTaxReport' },
    { value: 'monthlyReport', label: 'PeriodicWithholdingTaxReport' },
    { value: 'monthlyPeriodReport', label: 'PeriodicReportByMonths' },
];

const REPORTS_TAB = 0;
const PAYMENTS_TAB = 1;

const WithholdingModal = ({
    open,
    onClose,
    t,
    direction,
    withHoldingTaxType,
    setWithholdingTaxTypeFunc,
    isDateRange,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    date,
    setDate,
    dayjs,
    formattedAccountSourceDeductionCardOptions,
    selectedAccountCard,
    handleSelectedAccountCardChangeForWithholding,
    handleDownloadWithholdingReportReport,
    handleShowWithholdingReportReport,
    modalStyle: customModalStyle,
    client,
    partners = [],
    token
}) => {
    const [htmlContent, setHtmlContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
    const [showZeroBalanceCards, setShowZeroBalanceCards] = useState(true);
    const [useCreditAccount, setUseCreditAccount] = useState(false);
    const [originalHtmlContent, setOriginalHtmlContent] = useState('');
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [selectedSuppliers, setSelectedSuppliers] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const navigate = useNavigate();

    const handleShowReport = useCallback(async () => {
        if (!withHoldingTaxType) return;
        
        setIsLoading(true);
        try {
            // Pass empty string for supplierNameFilter on initial load - filtering happens client-side for display
            const html = await handleShowWithholdingReportReport(showZeroBalanceCards, '', useCreditAccount);
            // Always set originalHtmlContent when fetching fresh data
            setOriginalHtmlContent(html);
            setHtmlContent(html);
            // Reset search filter when new data is loaded
            setSupplierSearchTerm('');
            setSelectedSuppliers([]);
        } catch (error) {
            console.error('Error showing report:', error);
        } finally {
            setIsLoading(false);
        }
    }, [withHoldingTaxType, showZeroBalanceCards, useCreditAccount, handleShowWithholdingReportReport]);

    // Fetch supplier/client names from backend when modal opens or withholding type changes
    useEffect(() => {
        const fetchSupplierNames = async () => {
            if (!open || !client?.caseId || !token) return;
            
            try {
                let names = [];
                
                // Determine if this is a supplier or client report based on withHoldingTaxType
                // For now, we'll fetch supplier names - you can extend this logic based on report type
                if (withHoldingTaxType && withHoldingTaxType !== 'yearlyCertificate') {
                    names = await ReportsService.getSupplierNamesForWithholdingTax(client.caseId, token);
                }
                
                // Ensure names is an array and create clean objects to avoid circular refs
                const suppliersList = Array.isArray(names) 
                    ? names
                        .filter(name => name && typeof name === 'string')
                        .map(name => {
                            // Create a plain object with only value and label
                            return {
                                value: String(name),
                                label: String(name)
                            };
                        })
                    : [];
                    
                setSupplierOptions(suppliersList);
            } catch (error) {
                console.error('Error fetching supplier names:', error);
                setSupplierOptions([]);
            }
        };
        
        fetchSupplierNames();
    }, [open, client?.caseId, token, withHoldingTaxType]);

    // Navigation handler for row clicks
    const handleRowClick = useCallback(async (accountCardId, accountCardName, palCode) => {
        
        if (!client || !accountCardId || !accountCardName) {
            return;
        }

        try {
            const navigationState = {
                client,
                accountCardId: accountCardId,
                accountCardName: accountCardName,
                palCodeName: palCode || 'NonPalCode', // Use dynamic palCode or fallback
                startDate,
                endDate,
                isSortCodeReport: true,
                breadcrumbs: ['Reports', 'LedgerReport'],
            };
            

            
            // Navigate to AccountCardDashboard with the data
            navigate('/AccountCardDashboard', {
                state: navigationState,
            });
        } catch (error) {
            console.error('Error navigating to AccountCardDashboard:', error);
        }
    }, [client, startDate, endDate, navigate]);

    // Filter function to apply search and zero balance filters
    const applyFilters = useCallback(() => {
        if (!originalHtmlContent) return;

        // Create a temporary DOM element to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = originalHtmlContent;

        // Check if this is a multi-table report (monthly report with showMode=true)
        const allTables = tempDiv.querySelectorAll('table');
        const isMultiTableReport = allTables.length > 1;

        // Get selected supplier names array
        const selectedSupplierNames = selectedSuppliers.map(s => s.value || s);

        if (isMultiTableReport) {
            // Handle multi-table reports (monthly with showMode=true)
            
            // Process each table separately
            allTables.forEach((table, tableIndex) => {
                const tbody = table.querySelector('tbody');
                if (!tbody) return;

                // Get all rows from this table
                const allRows = tbody.querySelectorAll('tr');
                const dataRows = Array.from(allRows).filter(row => {
                    const firstCell = row.querySelector('td:first-child');
                    return firstCell && 
                           !firstCell.textContent.includes('סה״כ בחודש') && 
                           !firstCell.textContent.includes('סה״כ בדוח') &&
                           !firstCell.textContent.includes('מספר ספקים');
                });

                let filteredRows = Array.from(dataRows);

                // Apply both supplier search filter and zero balance filter together
                filteredRows = filteredRows.filter(row => {
                    // Apply supplier search filter (exact match for multiple suppliers)
                    if (selectedSupplierNames.length > 0) {
                        const supplierNameCell = row.querySelector('td:first-child');
                        if (supplierNameCell) {
                            const supplierName = supplierNameCell.textContent.trim();
                            if (!selectedSupplierNames.includes(supplierName)) {
                                return false; // Skip this row if it doesn't match any selected supplier
                            }
                        }
                    }

                    // Apply zero balance filter
                    if (!showZeroBalanceCards) {
                        const cells = row.querySelectorAll('td');
                        if (cells.length === 3) {
                            // 3-column structure
                            const totalBalanceText = cells[1]?.textContent?.trim() || '0';
                            const totalWithholdingText = cells[2]?.textContent?.trim() || '0';
                            
                            const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                            const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                            
                            const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                            const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                            
                            const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                            const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                            
                            return totalBalance !== 0 || totalWithholding !== 0;
                        } else if (cells.length === 5) {
                            // 5-column structure
                            const totalBalanceText = cells[3]?.textContent?.trim() || '0';
                            const totalWithholdingText = cells[4]?.textContent?.trim() || '0';
                            
                            const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                            const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                            
                            const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                            const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                            
                            const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                            const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                            
                            return totalBalance !== 0 || totalWithholding !== 0;
                        }
                        return true;
                    }
                    
                    return true; // If showZeroBalanceCards is true, include all rows
                });

                // Clear existing rows and add filtered rows
                tbody.innerHTML = '';
                filteredRows.forEach(row => {
                    const clonedRow = row.cloneNode(true);
                    tbody.appendChild(clonedRow);
                });

                // Add monthly subtotal if this is not the last table (final totals table)
                if (tableIndex < allTables.length - 1) {
                    const monthName = table.previousElementSibling?.querySelector('p')?.textContent?.trim() || '';
                    if (monthName) {
                        // Calculate totals for this month
                        let monthBalance = 0;
                        let monthWithholding = 0;
                        
                        filteredRows.forEach(row => {
                            const cells = row.querySelectorAll('td');
                            if (cells.length === 3) {
                                const balanceText = cells[1]?.textContent?.trim() || '0';
                                const withholdingText = cells[2]?.textContent?.trim() || '0';
                                
                                const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                                const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                                
                                const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                                const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                                
                                const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                                const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                                
                                monthBalance += balance;
                                monthWithholding += withholding;
                            } else if (cells.length === 5) {
                                const balanceText = cells[3]?.textContent?.trim() || '0';
                                const withholdingText = cells[4]?.textContent?.trim() || '0';
                                
                                const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                                const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                                
                                const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                                const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                                
                                const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                                const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                                
                                monthBalance += balance;
                                monthWithholding += withholding;
                            }
                        });
                        
                        // Always add monthly subtotal
                        const subtotalRow = document.createElement('tr');
                        subtotalRow.style.borderTop = '1px solid black';
                        subtotalRow.innerHTML = `
                            <td style="font-weight: bold;">סה״כ בחודש ${monthName}:</td>
                            <td style="font-weight: bold;">${monthBalance.toLocaleString('he-IL')}</td>
                            <td style="font-weight: bold;">${monthWithholding.toLocaleString('he-IL')}</td>
                        `;
                        tbody.appendChild(subtotalRow);
                    }
                } else {
                    // This is the final totals table - calculate overall totals
                    let totalBalance = 0;
                    let totalWithholding = 0;
                    let supplierCount = 0;

                    // Calculate totals from all previous tables
                    for (let i = 0; i < allTables.length - 1; i++) {
                        const prevTable = allTables[i];
                        const prevTbody = prevTable.querySelector('tbody');
                        if (prevTbody) {
                            const prevRows = prevTbody.querySelectorAll('tr');
                            prevRows.forEach(row => {
                                const cells = row.querySelectorAll('td');
                                if ((cells.length === 3 || cells.length === 5) && !cells[0].textContent.includes('סה״כ בחודש')) {
                                    supplierCount++;
                                    
                                    let balanceText, withholdingText;
                                    if (cells.length === 3) {
                                        balanceText = cells[1]?.textContent?.trim() || '0';
                                        withholdingText = cells[2]?.textContent?.trim() || '0';
                                    } else if (cells.length === 5) {
                                        balanceText = cells[3]?.textContent?.trim() || '0';
                                        withholdingText = cells[4]?.textContent?.trim() || '0';
                                    }
                                    
                                    const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                                    const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                                    
                                    const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                                    const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                                    
                                    const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                                    const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                                    
                                    totalBalance += balance;
                                    totalWithholding += withholding;
                                }
                            });
                        }
                    }
                    
                    // Always add final totals (even when filtered)
                    const totalsRow = document.createElement('tr');
                    totalsRow.style.borderTop = '2px solid black';
                    totalsRow.innerHTML = `
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">סה״כ בדוח:</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalBalance.toLocaleString('he-IL')}</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalWithholding.toLocaleString('he-IL')}</td>
                    `;
                    tbody.appendChild(totalsRow);
                    
                    // Add supplier count
                    const countRow = document.createElement('tr');
                    countRow.innerHTML = `
                        <td colspan="3" style="font-weight: bold; font-size: 14px; padding-top: 10px; text-align: right;">מספר ספקים בדוח: ${supplierCount}</td>
                    `;
                    tbody.appendChild(countRow);

                    // const clientCountRow = document.createElement('tr');
                    // clientCountRow.style.borderTop = '1px solid black';
                    // clientCountRow.innerHTML = `
                    //     <td style="font-weight: bold;">מספר ספקים עם ניכוי מס: ${supplierCount}</td>
                    // `;
                    // tbody.appendChild(clientCountRow);
                }
            });
        } else {
            // Handle single table reports (yearly reports or monthly with showMode=false)
            const allRows = tempDiv.querySelectorAll('tbody tr');
            const dataRows = Array.from(allRows).filter(row => {
                const firstCell = row.querySelector('td:first-child');
                return firstCell && !firstCell.textContent.includes('סה״כ ספקים בדוח');
            });
        
            let filteredRows = Array.from(dataRows);

            // Apply both supplier search filter and zero balance filter together
            filteredRows = filteredRows.filter(row => {
                // Apply supplier search filter (exact match for multiple suppliers)
                if (selectedSupplierNames.length > 0) {
                    const supplierNameCell = row.querySelector('td:first-child');
                    if (supplierNameCell) {
                        const supplierName = supplierNameCell.textContent.trim();
                        if (!selectedSupplierNames.includes(supplierName)) {
                            return false; // Skip this row if it doesn't match any selected supplier
                        }
                    }
                }

                // Apply zero balance filter
                if (!showZeroBalanceCards) {
                    const cells = row.querySelectorAll('td');
                    
                    if (cells.length === 3) {
                        const totalBalanceText = cells[1]?.textContent?.trim() || '0';
                        const totalWithholdingText = cells[2]?.textContent?.trim() || '0';
                        
                        const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                        const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                        
                        const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                        const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                        
                        const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                        const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                        
                        return totalBalance !== 0 || totalWithholding !== 0;
                    } else if (cells.length === 5) {
                        const totalBalanceText = cells[3]?.textContent?.trim() || '0';
                        const totalWithholdingText = cells[4]?.textContent?.trim() || '0';
                        
                        const isBalanceNegative = totalBalanceText.includes('(') && totalBalanceText.includes(')');
                        const isWithholdingNegative = totalWithholdingText.includes('(') && totalWithholdingText.includes(')');
                        
                        const cleanBalanceText = totalBalanceText.replace(/[(),]/g, '');
                        const cleanWithholdingText = totalWithholdingText.replace(/[(),]/g, '');
                        
                        const totalBalance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                        const totalWithholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                        
                        return totalBalance !== 0 || totalWithholding !== 0;
                    }
                    return true;
                }
                
                return true; // If showZeroBalanceCards is true, include all rows
            });

            // Rebuild the table with filtered rows
            const tbody = tempDiv.querySelector('tbody');
            if (tbody) {
                tbody.innerHTML = '';
                filteredRows.forEach(row => {
                    const clonedRow = row.cloneNode(true);
                    tbody.appendChild(clonedRow);
                });

                // Calculate totals from filtered data rows only
                let totalBalance = 0;
                let totalWithholding = 0;
                let supplierCount = filteredRows.length;

                filteredRows.forEach(row => {
                    const cells = row.querySelectorAll('td');
                    
                    if (cells.length === 3) {
                        const balanceText = cells[1]?.textContent?.trim() || '0';
                        const withholdingText = cells[2]?.textContent?.trim() || '0';
                        
                        const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                        const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                        
                        const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                        const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                        
                        const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                        const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                        
                        totalBalance += balance;
                        totalWithholding += withholding;
                    } else if (cells.length === 5) {
                        const balanceText = cells[3]?.textContent?.trim() || '0';
                        const withholdingText = cells[4]?.textContent?.trim() || '0';
                        
                        const isBalanceNegative = balanceText.includes('(') && balanceText.includes(')');
                        const isWithholdingNegative = withholdingText.includes('(') && withholdingText.includes(')');
                        
                        const cleanBalanceText = balanceText.replace(/[(),]/g, '');
                        const cleanWithholdingText = withholdingText.replace(/[(),]/g, '');
                        
                        const balance = parseFloat(cleanBalanceText) * (isBalanceNegative ? -1 : 1) || 0;
                        const withholding = parseFloat(cleanWithholdingText) * (isWithholdingNegative ? -1 : 1) || 0;
                        
                        totalBalance += balance;
                        totalWithholding += withholding;
                    }
                });

                // Add totals row with enhanced styling
                const totalsRow = document.createElement('tr');
                totalsRow.style.borderTop = '2px solid black';
                
                const firstRowCells = filteredRows.length > 0 ? filteredRows[0].querySelectorAll('td').length : 0;
                
                if (firstRowCells === 3) {
                    totalsRow.innerHTML = `
                        <td colspan="1" style="font-weight: bold; font-size: 14px; padding-top: 10px;">סה״כ ספקים בדוח: ${supplierCount}</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalBalance.toLocaleString('he-IL')}</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalWithholding.toLocaleString('he-IL')}</td>
                    `;
                } else if (firstRowCells === 5) {
                    totalsRow.innerHTML = `
                        <td colspan="3" style="font-weight: bold; font-size: 14px; padding-top: 10px;">סה״כ ספקים בדוח: ${supplierCount}</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalBalance.toLocaleString('he-IL')}</td>
                        <td style="font-weight: bold; font-size: 16px; padding-top: 15px;">${totalWithholding.toLocaleString('he-IL')}</td>
                    `;
                } else {
                    totalsRow.innerHTML = `
                        <td style="font-weight: bold; font-size: 14px; padding-top: 10px;">סה״כ ספקים בדוח: ${supplierCount}</td>
                    `;
                }
                tbody.appendChild(totalsRow);
            }
        }

        // Update the displayed content
        setHtmlContent(tempDiv.innerHTML);
    }, [originalHtmlContent, selectedSuppliers, showZeroBalanceCards]);

    // Apply filters when selected suppliers or zero balance toggle changes
    useEffect(() => {
        if (originalHtmlContent) {
            if (selectedSuppliers && selectedSuppliers.length > 0) {
                // Apply filters when suppliers are selected
                applyFilters();
            } else {
                // If no suppliers selected, show original content
                setHtmlContent(originalHtmlContent);
            }
        }
    }, [applyFilters, selectedSuppliers, originalHtmlContent]);

    const refreshReport = useCallback(async (showZero, creditAccount, errorLabel) => {
        if (!withHoldingTaxType || !originalHtmlContent) return;
        setIsLoading(true);
        try {
            const html = await handleShowWithholdingReportReport(showZero, '', creditAccount);
            setOriginalHtmlContent(html);
            setHtmlContent(html);
            setSupplierSearchTerm('');
            setSelectedSuppliers([]);
        } catch (error) {
            console.error(errorLabel, error);
        } finally {
            setIsLoading(false);
        }
    }, [withHoldingTaxType, originalHtmlContent, handleShowWithholdingReportReport]);

    const handleCreditAccountChange = (checked) => {
        setUseCreditAccount(checked);
        refreshReport(showZeroBalanceCards, checked, 'Error refreshing report (credit account):');
    };

    const handleZeroBalanceChange = (checked) => {
        setShowZeroBalanceCards(checked);
        refreshReport(checked, useCreditAccount, 'Error updating report with filter:');
    };

    const handleClearFilters = () => {
        setSupplierSearchTerm('');
        setSelectedSuppliers([]);
        setShowZeroBalanceCards(true);
    };

    const handleSupplierChange = (values) => {
        setSelectedSuppliers(values.map((v) => ({ value: String(v.value), label: String(v.label) })));
    };

    const handleDownload = () => {
        const supplierNames = selectedSuppliers.map(s => s.value || s).join(',');
        handleDownloadWithholdingReportReport(showZeroBalanceCards, supplierNames, useCreditAccount);
    };

    const handleFrameLoad = (e) => {
        const iframe = e.target;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        if (!iframeDoc) return;

        iframeDoc.querySelectorAll('tbody tr').forEach((row) => {
            const accountCardId = row.getAttribute('data-id');
            const accountCardName = row.querySelector('td:first-child')?.textContent?.trim();
            const palCode = row.getAttribute('data-type');

            if (accountCardId && accountCardName) {
                row.style.cursor = 'pointer';
                row.style.transition = 'background-color 0.2s ease';
                row.addEventListener('click', () => {
                    handleRowClick(accountCardId, accountCardName, palCode);
                });
            }
        });
    };

    const modalTitle = [t('WithholdingTaxReports'), client?.name].filter(Boolean).join(' · ');
    const selectedCard = selectedAccountCard?.value != null ? selectedAccountCard : null;

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            title={modalTitle}
            direction={direction}
            expanded
            tabs={[
                { key: REPORTS_TAB, label: t('WithholdingReportsTab') },
                { key: PAYMENTS_TAB, label: t('CreatePaymentsTab') },
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            footer={activeTab === REPORTS_TAB ? (
                <>
                    <XtramButton variant="secondary" onClick={onClose}>{t('Close')}</XtramButton>
                    <XtramButton onClick={handleDownload} disabled={!withHoldingTaxType}>
                        <DownloadIcon fontSize="small" />
                        {t('Download')}
                    </XtramButton>
                </>
            ) : null}
        >
            {activeTab === PAYMENTS_TAB ? (
                <div className={styles.tabPanel}>
                    <WithholdingUnpaidPaymentsTab
                        t={t}
                        direction={direction}
                        client={client}
                        token={token}
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                    />
                </div>
            ) : (
                <div className={styles.content}>
                    <section className={styles.controls}>
                        <ReportTypePicker
                            t={t}
                            types={REPORT_TYPES}
                            value={withHoldingTaxType}
                            onChange={setWithholdingTaxTypeFunc}
                        />
                        <div className={styles.controlsRow}>
                            <PeriodFields
                                t={t}
                                isDateRange={isDateRange}
                                startDate={startDate}
                                endDate={endDate}
                                setStartDate={setStartDate}
                                setEndDate={setEndDate}
                                date={date}
                                setDate={setDate}
                            />
                            {withHoldingTaxType === 'yearlyCertificate' && (
                                <XtramField label={t('Select AccountCard')} className={styles.cardField}>
                                    <XtramSelect
                                        direction={direction}
                                        options={formattedAccountSourceDeductionCardOptions}
                                        value={selectedCard}
                                        placeholder={t('Select AccountCard')}
                                        onChange={(option) => handleSelectedAccountCardChangeForWithholding(null, option)}
                                    />
                                </XtramField>
                            )}
                            <div className={styles.checkField}>
                                <XtramCheckbox
                                    checked={useCreditAccount}
                                    onChange={handleCreditAccountChange}
                                    label={t('UseCreditAccount')}
                                />
                            </div>
                            <XtramButton
                                className={styles.viewBtn}
                                onClick={handleShowReport}
                                disabled={!withHoldingTaxType || isLoading}
                            >
                                <VisibilityIcon fontSize="small" />
                                {isLoading ? t('Loading') : t('ViewData')}
                            </XtramButton>
                        </div>
                    </section>

                    {htmlContent && (
                        <div className={styles.filters}>
                            <span className={styles.filtersLabel}>
                                <FilterAltIcon fontSize="small" />
                                {t('Filters')}
                            </span>
                            <div className={styles.filterField}>
                                <XtramSelect
                                    direction={direction}
                                    multiple
                                    options={supplierOptions}
                                    value={selectedSuppliers}
                                    placeholder={t('SelectSupplier')}
                                    onChange={handleSupplierChange}
                                />
                            </div>
                            <XtramCheckbox
                                checked={showZeroBalanceCards}
                                onChange={handleZeroBalanceChange}
                                label={t('ShowZeroBalance')}
                            />
                            <XtramButton variant="ghost" small className={styles.clearBtn} onClick={handleClearFilters}>
                                <ClearAllIcon fontSize="small" />
                                {t('ClearFilters')}
                            </XtramButton>
                        </div>
                    )}

                    <ReportPreview
                        t={t}
                        html={htmlContent}
                        loading={isLoading}
                        title="Report Preview"
                        sandbox="allow-same-origin"
                        onLoad={handleFrameLoad}
                    />
                </div>
            )}
        </XtramModal>
    );
};

export default WithholdingModal;
